﻿import { executeLegacyMethod, isLegacyMethod } from './compatCuiMethods';
import cuiCommands from './commands/index';
import getMethod from '../metadata/method';
import itemTypeMetadata from '../metadata/itemType';
import getResource from '../core/resources';
import alert from '../components/alert';

const alertError = (message, error) => {
	const { message: technicalMessage } = error;
	const stackTrace = getResource('common.client_side_err');
	return alert(message, {
		type: 'stack',
		technicalMessage,
		stackTrace
	});
};

const notifyError = (message) => {
	const notify = aras.getNotifyByContext(window);
	notify(message, { type: 'error' });
};

const callAttachedHandler = (
	handler,
	commandAlias,
	commandMethod,
	context,
	options
) => {
	if (handler) {
		const method = getMethod(handler);
		const code = method?.method_code;
		if (code !== '') {
			return cuiMethods.executeClientMethod(handler, context, options);
		}
	}

	if (!commandAlias) {
		return;
	}

	const { commandFactory } = cuiCommands;
	const command = commandFactory(commandAlias, options);
	if (!command) {
		const message = getResource('cui.failed_to_find_command', commandAlias);
		notifyError(message);
		return;
	}

	if (commandMethod === 'execute') {
		return command[commandMethod]().catch((error) => {
			const message = getResource(
				'cui.failed_to_execute_command',
				commandAlias
			);
			alertError(message, error);
		});
	}

	try {
		return command[commandMethod]();
	} catch (error) {
		const message = getResource('cui.failed_to_execute_command', commandAlias);
		alertError(message, error);
	}
};

const cuiMethods = {
	collectCuiData: function (items, initialData) {
		const data = Object.assign({}, initialData, {
			dataMap: new Map(),
			events: new Set(['click']),
			images: [],
			roots: []
		});

		return items.reduce(function (data, item) {
			item = cuiMethods.initializeItem(item, data);
			if (item.data) {
				const itemsMap = new Map();
				item.data.forEach(function (childItem) {
					childItem = cuiMethods.initializeItem(childItem, data, item);
					cuiMethods.addItemPropertiesToCuiData(data, childItem);
					itemsMap.set(childItem.id, childItem);
				});
				item.data = itemsMap;
			}
			cuiMethods.addItemPropertiesToCuiData(data, item);
			data.dataMap.set(item.id, item);
			data.roots.push(item.name);
			return data;
		}, data);
	},
	addItemPropertiesToCuiData: function (cuiData, item) {
		if (item.clickEventType) {
			if (item.clickEventType === 'mousedown') {
				cuiData.events.add('keydown');
			}
			cuiData.events.add(item.clickEventType);
		}
		if (item.image) {
			cuiData.images.push(item.image);
		}
		if (item.image_additional) {
			cuiData.images.push(item.image_additional);
		}
	},
	destroyMethodsCollection: new WeakMap(),
	destroyMethodFactory: function (control, detachEventListenersArray = []) {
		const destroy = () => {
			cuiMethods.destroyMethodsCollection.delete(control);
			detachEventListenersArray.forEach((fn) => fn());
		};
		return destroy;
	},
	defaultCuiItemHandler: function (control, options, itemId, event) {
		let parentItem;
		let targetItem;
		const menuItemNode = event.target.closest('li[data-index]');
		if (menuItemNode) {
			parentItem = control.data.get(itemId);
			const targetId = menuItemNode.dataset.index;
			targetItem = parentItem.data.get(targetId);
		} else {
			targetItem = control.data.get(itemId);
		}

		if (!targetItem) {
			return;
		}

		const clickEventType = targetItem.clickEventType;
		const eventType = event.type;
		const context = {
			currentTarget: parentItem || targetItem,
			target: targetItem,
			control: control
		};

		if (
			targetItem['@type'] === 'CommandBarButton' &&
			clickEventType === 'mousedown' &&
			['keydown'].includes(eventType) &&
			['Enter', 'NumpadEnter', 'Space', 13, 32].includes(
				event.code || event.keyCode
			)
		) {
			cuiMethods.executeClickMethod(targetItem, context, options);
			return;
		}

		if (
			clickEventType === eventType ||
			(!clickEventType && eventType === 'click')
		) {
			cuiMethods.executeClickMethod(targetItem, context, options);
		}
	},
	executeClientMethod: function (methodId, context, options = {}) {
		if (!methodId) {
			return;
		}

		const method = getMethod(methodId);
		if (!method) {
			const message = getResource('cui.failed_to_execute_method', methodId);
			notifyError(message);
			return;
		}

		if (!options.commandFactory) {
			const commandFactory = cuiMethods.getLayoutCommandFactory(options);
			context = {
				...context,
				commandFactory
			};
		}

		Object.assign(context, options);
		const methodCode = method.method_code;
		const isLegacy = isLegacyMethod(methodCode, context);
		try {
			if (isLegacy) {
				return executeLegacyMethod(methodId, methodCode, context);
			}

			const method = new Function('control', 'target', 'options', methodCode); // jshint ignore:line
			return method(context.control, context.target, context);
		} catch (error) {
			const message = getResource('aras_object.method_failed', method.name);
			alertError(message, error);
		}
	},
	executeClickMethod: (item = {}, ...args) => {
		const {
			'command_alias@keyed_name': commandAlias,
			on_click_handler: clickHandler,
			on_keydown_handler: keydownHandler
		} = item;
		const handler = clickHandler || keydownHandler;

		return callAttachedHandler(handler, commandAlias, 'execute', ...args);
	},
	executeInitMethod: (item = {}, ...args) => {
		const {
			'command_alias@keyed_name': commandAlias,
			on_init_handler: handler
		} = item;

		return callAttachedHandler(handler, commandAlias, 'canExecute', ...args);
	},
	getConfigurableUi: async function (location, options) {
		const requestParams = await cuiMethods.getRequestParams(location, options);
		return aras.MetadataCacheJson.GetConfigurableUiAsync(requestParams);
	},
	getRequestParams: async (location, options) => {
		const {
			itemId = '',
			itemTypeName = '',
			item_classification: itemClassification = ''
		} = options;

		const itemTypeId = itemTypeMetadata.getItemTypeId(itemTypeName);
		const presentableItem = await isPresentableItem(itemTypeId);

		return {
			item_id: presentableItem ? itemId : '',
			item_type_id: itemTypeId,
			location_name: location,
			item_classification: itemClassification
		};
	},
	getLayoutCommandFactory: ({ itemTypeName, location }) => {
		const { commandFactory } = cuiCommands;
		return (type, options) => {
			return commandFactory(type, { itemTypeName, location, ...options });
		};
	},
	handleControlEvents: function (control, events, options, handler) {
		if (!handler) {
			return;
		}
		if (cuiMethods.destroyMethodsCollection.has(control)) {
			throw new Error('The event handler for this control is already defined.');
		}

		const controlEventHandler = (...args) => {
			const controlData = cuiMethods.destroyMethodsCollection.get(control);
			const handlerOptions = controlData.options;

			return handler(control, handlerOptions, ...args);
		};
		const detachEventListenersArray = [...events].map((eventName) => {
			if (control.on) {
				return control.on(eventName, controlEventHandler);
			}

			control.addEventListener(eventName, controlEventHandler);
			return () => control.removeEventListener(eventName, controlEventHandler);
		});

		const destroy = cuiMethods.destroyMethodFactory(
			control,
			detachEventListenersArray
		);

		cuiMethods.destroyMethodsCollection.set(control, {
			destroy,
			options
		});

		return {
			destroy,
			updateOptions: (newOptions) => {
				const controlData = cuiMethods.destroyMethodsCollection.get(control);
				cuiMethods.destroyMethodsCollection.set(control, {
					...controlData,
					options: newOptions
				});
			}
		};
	},
	initializeCuiControl: function (control, location, cuiItemTypes, options) {
		const destroyMethodsCollection = cuiMethods.destroyMethodsCollection;
		if (destroyMethodsCollection.has(control)) {
			destroyMethodsCollection.get(control).destroy();
			destroyMethodsCollection.delete(control);
		}

		return cuiMethods
			.getConfigurableUi(location, options)
			.then(function (items) {
				const cuiData = cuiMethods.collectCuiData(items, {
					control,
					cuiItemTypes,
					options
				});
				ArasModules.SvgManager.load(cuiData.images);
				return cuiData;
			});
	},
	initializeItem: function (item, cuiData, rootItem) {
		const context = {
			control: cuiData.control,
			currentTarget: rootItem || item,
			target: item
		};
		const type = item['@type'];
		const itemTypeMetadata = cuiData.cuiItemTypes[type];
		const initHandlerResult = cuiMethods.executeInitMethod(
			item,
			context,
			cuiData.options
		);
		Object.assign(
			item,
			itemTypeMetadata,
			item.additional_data,
			initHandlerResult
		);

		item.id = item.name;
		return item;
	},
	reinitControlItems: function (control, eventType, options) {
		const context = {
			control: control
		};
		const reinitAllItems = function (data, rootItem) {
			data.forEach(function (item) {
				const events = item.include_events;
				if (events && events.indexOf(eventType) !== -1) {
					initItem(data, item, rootItem);
				}
			});
		};
		const initItem = function (data, item, rootItem) {
			Object.assign(context, {
				currentTarget: rootItem || item,
				target: item
			});
			const optionsForInit =
				options || cuiMethods.destroyMethodsCollection.get(control).options;
			const initHandlerResult = cuiMethods.executeInitMethod(
				item,
				context,
				optionsForInit
			);
			const newItem = Object.assign(
				{},
				item,
				item.additional_data,
				initHandlerResult
			);
			data.set(item.id, newItem);

			if (item.data) {
				reinitAllItems(item.data, item);
			}
		};

		reinitAllItems(control.data);
		control.render();
	},
	adaptDataForControl: function (cuiData) {
		const data = cuiData.dataMap;
		data.forEach(function (item) {
			if (item.image) {
				item.icon = item.image;
				delete item.image;
			}
			if (!item.data) {
				return;
			}
			item.data.forEach(function (child) {
				data.set(child.id, child);
			});
			item.children = item.roots;
			delete item.data;
			delete item.roots;
		});
		return cuiData;
	}
};

const isPresentableItem = async (itemTypeId) => {
	if (!itemTypeId) {
		return false;
	}

	const itemType = await itemTypeMetadata.getItemType('PresentableItems');
	const morphaeList = itemTypeMetadata.getMorphaeList(itemType);
	return morphaeList.some((item) => item.id === itemTypeId);
};

export default cuiMethods;
